import { useQuery } from '@tanstack/vue-query'
import { Ref, computed } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { DEFAULT_TRENDING_SIZE, TRENDING_REQUEST_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { useAppStore } from '@/store/app-store'
import { CategoryDataType, MainCategoriesType } from '@/types'
import { shuffle, unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { TRENDING_CACHE } from '@/utils/requests/utils'

import { usePersisterFn } from './persister-plugin'
import { Persisters } from './shared'

const PLACEHOLDER_DATA = {
    id: '',
    title: '',
    location: { name: '' },
    games: [],
}

export const useTrendingData = (options: { enabled?: Ref<boolean>; additionalKeys?: Ref<unknown[]> } = {}) => {
    const { currentLocale } = useI18nAddon()
    const { getCategoryRoute } = useUrl()
    const { getGamesByCategories } = useAPI()
    const appStore = useAppStore()

    // persister type depends on queryKey cardinality
    const queryKey = computed(() => [
        'trending_now',
        currentLocale.value,
        appStore.platform,
        appStore.isDebugMode,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        ...(options.additionalKeys?.value || []),
    ])
    // TODO shuffle in separate query, remove options.keys from queryKey and set cardinality to Low
    const persister = usePersisterFn(Persisters.MediumCardinality)

    const currentTrending = computed(() => data.value || PLACEHOLDER_DATA)
    const unzippedTrendingWithGames = computed(() => currentTrending.value.games.map(unzipShortGame))

    function mapCategory(data: CategoryDataType) {
        const [trending] = data.categories.map(({ id, hru, title, games }) => ({
            id: id as MainCategoriesType,
            title,
            location: getCategoryRoute({ hru, type: 'category' }),
            games: shuffle(games.map(zipAPIGame)).slice(0, DEFAULT_TRENDING_SIZE),
        }))
        return trending
    }
    const { data, suspense } = useQuery({
        queryKey,
        queryFn: () =>
            getGamesByCategories({
                categoriesIds: ['trending_now'],
                pagination: { page: 1, pageSize: TRENDING_REQUEST_SIZE },
                platform: appStore.platform,
                withDebugInfo: appStore.isDebugMode,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                locale: currentLocale.value,
            }).then((rawTrendingData) => mapCategory(rawTrendingData)),
        enabled: options.enabled,
        staleTime: TRENDING_CACHE,
        persister,
    })

    return {
        unzippedTrendingWithGames,
        currentTrending,
        preloadTrendingData: suspense,
    }
}
